import { useState, useCallback, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useServerPropertySceneMuxer } from '@/hooks/useServerPropertySceneMuxer';
import { usePropertyVideoAdapter } from '@/adapters/propertyVideoAdapter';
import pLimit from 'p-limit';
import type { IBulkProgress, ISceneForMuxing } from '@/@types/propertySceneEditor';
import type { Id } from '../../convex/_generated/dataModel';

const MUX_CONCURRENCY_LIMIT = 4;

interface IImageAnalysis {
  originalImageId: Id<'_storage'>;
  processedImageId16x9?: Id<'_storage'>;
  processedImageId9x16?: Id<'_storage'>;
  sceneType: string;
  description: string;
}

export function usePropertySceneEditorData(
  videoId: Id<'propertyVideos'>,
  propertyId: Id<'propertyListings'>,
  selectedAspectRatio: '16:9' | '9:16'
) {
  const [muxProgress, setMuxProgress] = useState<IBulkProgress>({
    status: 'idle',
    total: 0,
    completed: 0,
    failed: 0,
  });

  const { muxPropertyScene } = useServerPropertySceneMuxer();

  const scenesForMuxing = useQuery(api.property.queries.getPropertyScenesForMuxing, {
    propertyVideoId: videoId,
  });

  const {
    adapter: propertyVideoAdapter,
    isReady: isFullVideoReady,
    readyMessage: fullVideoReadyMessage,
  } = usePropertyVideoAdapter(videoId);

  const propertyVideo = useQuery(api.property.queries.getPropertyVideo, {
    videoId,
  });

  const finalVideoUrl = useQuery(
    api.storage.getUrl,
    propertyVideo?.finalVideoId ? { storageId: propertyVideo.finalVideoId } : 'skip'
  );

  const property = useQuery(
    api.property.queries.getPropertyListing,
    propertyId ? { propertyId } : 'skip'
  );

  const processedImages: Id<'_storage'>[] = useMemo(() => {
    if (!property) return [];

    const imageAnalyses = property.imageAnalyses as IImageAnalysis[] | undefined;

    if (!imageAnalyses || imageAnalyses.length === 0) {
      console.warn('[PropertySceneEditor] No imageAnalyses available - images not processed');
      return [];
    }

    const processedIds: Id<'_storage'>[] = [];
    for (const analysis of imageAnalyses) {
      const processedId =
        selectedAspectRatio === '9:16'
          ? analysis.processedImageId9x16
          : analysis.processedImageId16x9;

      if (processedId) {
        processedIds.push(processedId);
      }
    }

    if (processedIds.length === 0) {
      console.warn(
        `[PropertySceneEditor] No processed images for ${selectedAspectRatio} aspect ratio`
      );
    }

    return processedIds;
  }, [property, selectedAspectRatio]);

  const handleMuxAllScenes = useCallback(async () => {
    if (!scenesForMuxing || scenesForMuxing.length === 0) {
      return;
    }

    const total = scenesForMuxing.length;

    setMuxProgress({
      status: 'generating',
      total,
      completed: 0,
      failed: 0,
    });

    const limit = pLimit(MUX_CONCURRENCY_LIMIT);

    const promises = scenesForMuxing.map((scene: ISceneForMuxing) =>
      limit(async () => {
        try {
          await muxPropertyScene(scene.sceneId, scene.videoUrl, scene.audioUrl);
          setMuxProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
        } catch (error) {
          console.error(`[PropertySceneEditor] Failed to mux scene ${scene.sceneNumber}:`, error);
          setMuxProgress(prev => ({ ...prev, failed: prev.failed + 1 }));
        }
      })
    );

    await Promise.all(promises);

    setMuxProgress(prev => {
      const finalStatus =
        prev.failed === total ? 'failed' : prev.failed > 0 ? 'partial' : 'completed';
      return {
        status: finalStatus,
        total,
        completed: prev.completed,
        failed: prev.failed,
      };
    });
  }, [scenesForMuxing, muxPropertyScene]);

  return {
    processedImages,
    muxProgress,
    handleMuxAllScenes,
    propertyVideoAdapter,
    isFullVideoReady,
    fullVideoReadyMessage,
    finalVideoUrl: finalVideoUrl ?? null,
  };
}
